import { useCallback, useEffect, useState } from 'react';
import { ErrorState } from '@tessma/design-system';
import { ApiError } from '../work/gateway.js';

/**
 * The three things every data-backed screen in this application needs, in one
 * place: loading a request, showing the API's own refusal, and rendering money
 * and dates without doing arithmetic on either.
 *
 * EXTRACTED IN STAGE 8 rather than copied. Stage 6 had `useAsync` and `Refusal`
 * private to `analytics/screens.tsx`; Finance needs exactly the same two, and a
 * second copy would be a second place for the refusal rule to drift. The rule
 * is load-bearing enough to deserve one implementation.
 */

export type AsyncState<T> =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly value: T }
  | { readonly status: 'failed'; readonly error: ApiError };

export function useAsync<T>(load: () => Promise<T>, deps: readonly unknown[]) {
  const [state, setState] = useState<AsyncState<T>>({ status: 'loading' });

  const run = useCallback(() => {
    setState({ status: 'loading' });
    load().then(
      (value) => {
        setState({ status: 'ready', value });
      },
      (error: unknown) => {
        setState({
          status: 'failed',
          error:
            error instanceof ApiError
              ? error
              : new ApiError(0, 'Could not reach the server', 'Check your connection and retry.'),
        });
      },
    );
    // `load` is a fresh closure every render, so it cannot be the dependency —
    // depending on it would reload on every render forever. The list the CALLER
    // passes is the real one: what the request actually varies with.
  }, deps);

  useEffect(run, [run]);
  return { state, reload: run };
}

/**
 * The API's own words, shown as the API worded them.
 *
 * A 404 and a 403 mean different things and the SERVER decided which this was.
 * The browser repeating that decision — inferring "you lack permission" from a
 * 404, say — would undo the concealment the ordering exists to provide.
 */
export function Refusal({
  error,
  onRetry,
}: {
  readonly error: ApiError;
  readonly onRetry?: () => void;
}) {
  return (
    <ErrorState
      title={error.message}
      description={error.detail ?? 'The server did not say more.'}
      {...(onRetry === undefined ? {} : { onRetry })}
    />
  );
}

/**
 * THE SERVER'S SENTENCE, for an inline refusal.
 *
 * `ApiError.message` is the problem document's TITLE — "Cannot do that" —
 * and its `detail` is the sentence somebody can act on: "the opening trial
 * balance does not balance by 250 minor units", "that document has 100000
 * minor units outstanding", "only opening balances may be posted until the
 * entity goes live". Twenty-one call sites across Finance rendered
 * `error.message` under a comment promising the server's words, and threw
 * the words away.
 *
 * So: the detail when there is one, the title when there is not, and the
 * caller's own fallback only when the failure was not an `ApiError` at all —
 * a network error, say, which the server never described because it never
 * heard the request.
 */
export function refusalText(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.detail ?? error.message;
  return error instanceof Error ? error.message : fallback;
}

/** Currencies whose minor unit is not two decimal places. */
const EXPONENTS: Readonly<Record<string, number>> = { JPY: 0, KRW: 0, ISK: 0, BHD: 3, KWD: 3 };

/**
 * Minor units as a string, formatted for a human — BY SLICING DIGITS, never by
 * dividing.
 *
 * `Number('12345678901234567')/100` is a double and has already lost the answer
 * by the time it is rendered. This inserts a decimal point into the string the
 * server sent, so what appears on screen is what the ledger holds, digit for
 * digit, at any magnitude.
 *
 * IT IS PRESENTATION AND NOTHING ELSE. Nothing formatted here is ever parsed
 * back into a request: a value that leaves this browser leaves as the minor
 * units it arrived as.
 */
export function formatMoney(minorUnits: string, currency: string): string {
  const exponent = EXPONENTS[currency] ?? 2;
  const negative = minorUnits.startsWith('-');
  const digits = (negative ? minorUnits.slice(1) : minorUnits).replace(/^0+(?=\d)/, '');
  const padded = digits.padStart(exponent + 1, '0');
  const whole = exponent === 0 ? padded : padded.slice(0, -exponent);
  const fraction = exponent === 0 ? '' : `.${padded.slice(-exponent)}`;
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}${currency} ${grouped}${fraction}`;
}

/** A `YYYY-MM-DD` day, as a human reads it. Null becomes a dash, never a zero. */
export function formatDay(day: string | null): string {
  if (day === null || day === '') return '—';
  const parsed = new Date(`${day}T00:00:00Z`);
  return Number.isNaN(parsed.getTime())
    ? day
    : parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function formatWhen(iso: string | null): string {
  if (iso === null) return '—';
  return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}
